import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  Avatar,
  Badge,
  Button,
  EmptyState,
  InlineError,
  Panel,
  Skeleton,
} from '../../components/ui';
import { PageHeader } from '../../layouts/Shells';
import {
  useClearReadNotifications,
  useDeleteNotification,
  useInbox,
  useMarkAllNotificationsRead,
  useSetNotificationRead,
} from './hooks';
import {
  NOTIFICATION_EVENT_BADGES,
  NOTIFICATION_EVENT_LABELS,
  INBOX_TABS,
  cgPipelineLabel,
  filterInboxByTab,
  filterInboxItems,
  filtersForTab,
  inboxSubject,
  isMentionWithoutAccess,
  mentionProjectName,
  isLegacySubmissionPing,
  isMessagesNotification,
  notificationHref,
  unreadCount,
} from './types';
import type { InboxItem, InboxTabKey, NotificationFilterKey } from './types';

/**
 * The Inbox (handoff 23): everything the database decided this person should
 * know, newest first. Every row was written by a trigger or the daily
 * due-date job for this recipient alone — nothing here is computed
 * client-side, and nobody else's Inbox is reachable (Stage 2 assertions).
 *
 * The redesign spec turns this into a split pane: the list reads as one
 * borderless column, and selecting a row opens it in the reading pane beside
 * it rather than navigating away. Selecting clears the unread state; the
 * reading pane's Open button — and the task link in the row itself — are the
 * deep links into the project canvas.
 *
 * Nothing is selected on arrival on purpose: auto-selecting the newest row
 * would silently mark it read on every visit.
 *
 * A pointer whose task or project has since become unreadable renders as
 * unavailable rather than vanishing: access removal takes effect
 * immediately, history does not rewrite itself.
 */

/** The two job-written events have no human actor; they narrate themselves. */
function itemSentence(item: InboxItem): string {
  if (item.event_type === 'huddle_comment') {
    return `Alex commented on ${item.task_title ?? 'a task'}`;
  }
  // A legacy supplier ping carries the 'mentioned' event and is not a mention
  // (20261121000100). Its own rows are not being rewritten, so the sentence is
  // corrected on the way out rather than in the database.
  const label = isLegacySubmissionPing(item)
    ? NOTIFICATION_EVENT_LABELS.sample_photos_uploaded
    : NOTIFICATION_EVENT_LABELS[item.event_type];
  if (item.actor_name === '') {
    return label;
  }
  return `${item.actor_name} ${label}`;
}

/**
 * What the row names after the sentence, and whether it can be opened.
 *
 * Three sources, in order of how specific they are: the task, the CG order,
 * and — for rows that point at neither — whatever the details can tell us.
 * The last one exists because a row that names nothing is unreadable, which
 * is the whole defect Kyu reported: "cannot see which task or project".
 */
function rowTarget(item: InboxItem): { text: string; href: string } | null {
  // The huddle sentence already contains the task title. Returning it again
  // would render "Alex commented on Task: Task".
  if (item.event_type === 'huddle_comment') {
    return null;
  }
  // Drop 3c: a thread reply has no task. It names the room (details.room_name,
  // a snapshot the trigger writes) and links to the thread (notificationHref).
  if (item.event_type === 'thread_reply') {
    const room = item.details['room_name'];
    return {
      text: typeof room === 'string' && room.trim() !== '' ? room : 'a thread',
      href: notificationHref(item),
    };
  }
  // npc r3: any other Messages notice without a task (an @ in a room, DM or
  // group, or being added to a room) names the room and links to the message,
  // the same way a thread reply does above.
  if (isMessagesNotification(item) && item.task_title === null && item.task_id === null) {
    return {
      text: mentionProjectName(item) ?? item.project_name ?? 'a message',
      href: notificationHref(item),
    };
  }
  // Hiring Phase 2: no task or project. The row still needs something to click.
  if (item.event_type === 'hiring_assigned' || item.event_type === 'hiring_alert') {
    return {
      text: item.event_type === 'hiring_assigned' ? 'an applicant' : 'Hiring',
      href: notificationHref(item),
    };
  }
  if (item.task_title !== null) {
    return { text: item.task_title, href: notificationHref(item) };
  }
  if (item.cg_order_no !== null && item.cg_order_no !== '') {
    return { text: `Order ${item.cg_order_no}`, href: notificationHref(item) };
  }
  const subject = inboxSubject(item);
  return subject === null ? null : { text: subject, href: notificationHref(item) };
}

function itemTime(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** System rows carry no actor: the app itself stands in for the due-date job. */
function actorLabel(item: InboxItem): string {
  return item.actor_name === '' ? 'TAP Hub' : item.actor_name;
}

function isUnavailable(item: InboxItem): boolean {
  // A mention of somebody outside the project reads as unavailable by this
  // test — project set, name unreadable — but it is not. Nothing was lost;
  // they were never on it, and the row carries its own name and its own
  // sentence. Telling them it is "no longer available" would describe a
  // change that never happened.
  if (isMentionWithoutAccess(item)) {
    return false;
  }
  return item.project_id !== null && item.project_name === null;
}

function InboxRow({
  item,
  selected,
  onToggleRead,
  onDismiss,
  onSelect,
  pending,
}: {
  item: InboxItem;
  selected: boolean;
  onToggleRead: (item: InboxItem) => void;
  onDismiss: (item: InboxItem) => void;
  onSelect: (item: InboxItem) => void;
  pending: boolean;
}) {
  const unavailable = isUnavailable(item);
  const noAccessMention = isMentionWithoutAccess(item);
  const target = rowTarget(item);
  const badge = isLegacySubmissionPing(item)
    ? NOTIFICATION_EVENT_BADGES.sample_photos_uploaded
    : NOTIFICATION_EVENT_BADGES[item.event_type];
  const pipeline = cgPipelineLabel(item);

  return (
    <li
      className={`inbox-row${item.is_read ? ' inbox-row--read' : ''}${
        selected ? ' inbox-row--selected' : ''
      }`}
      onClick={() => onSelect(item)}
    >
      <span className="inbox-unread-dot" aria-hidden="true" />
      <Avatar name={actorLabel(item)} imageUrl={item.actor_avatar_url} size="xs" />
      <div className="inbox-row-main">
        <p className="inbox-sentence">
          {itemSentence(item)}
          {target === null ? null : (
            <>
              {': '}
              {unavailable || target.href === '' ? (
                <span className="inbox-task-title">{target.text}</span>
              ) : (
                <Link
                  className="inbox-task-link"
                  to={target.href}
                  onClick={(event) => event.stopPropagation()}
                >
                  {target.text}
                </Link>
              )}
            </>
          )}
        </p>
        <p className="inbox-row-meta">
          <Badge tone={badge.tone} micro>
            {badge.label}
          </Badge>
          {/* Which pipeline the order runs on, on CG rows only. Kyu asked for
              it for the same reason he asked for it on the Orders table: with
              both kinds in one bucket, a row that does not say which one it is
              blurs into the other. Named rather than colour-coded, and absent
              entirely when the order is unreadable — a guessed pipeline is
              worse than none. */}
          {pipeline === '' ? null : (
            <Badge tone={pipeline === 'In-Stock' ? 'brand' : 'neutral'} micro>
              {pipeline}
            </Badge>
          )}
          {unavailable ? (
            <span>No longer available to you</span>
          ) : noAccessMention ? (
            <span>{mentionProjectName(item) ?? 'a project'} — you’re not on this project</span>
          ) : item.project_name === null ? null : (
            <span>{item.project_name}</span>
          )}
          <span>{itemTime(item.created_at)}</span>
        </p>
      </div>
      <div className="inbox-row-actions" onClick={(event) => event.stopPropagation()}>
        <button
          type="button"
          className="inbox-icon-button"
          onClick={() => onToggleRead(item)}
          disabled={pending}
          aria-label={`Mark ${item.is_read ? 'unread' : 'read'}: ${itemSentence(item)}`}
          title={item.is_read ? 'Mark unread' : 'Mark read'}
        >
          <span aria-hidden="true">{item.is_read ? '↺' : '✓'}</span>
        </button>
        <button
          type="button"
          className="inbox-icon-button"
          onClick={() => onDismiss(item)}
          disabled={pending}
          aria-label={`Dismiss: ${itemSentence(item)}`}
          title="Dismiss"
        >
          <span aria-hidden="true">✕</span>
        </button>
      </div>
    </li>
  );
}

/** The reading pane: the selected row, opened out. */
function InboxDetail({
  item,
  onOpen,
  onDismiss,
  pending,
}: {
  item: InboxItem | null;
  onOpen: (item: InboxItem) => void;
  onDismiss: (item: InboxItem) => void;
  pending: boolean;
}) {
  if (item === null) {
    return (
      <div className="inbox-detail inbox-detail--empty">
        <p>Pick a notification to read it here.</p>
      </div>
    );
  }

  const unavailable = isUnavailable(item);
  const noAccessMention = isMentionWithoutAccess(item);
  // Whether there is somewhere to go, asked of the function that decides where
  // that is. Gating on project_id made every CG order notification a dead end:
  // those rows carry cg_order_id and a null project_id by design, so the one
  // working alert in the CG flow arrived with no way to open what it was about.
  const linkable = !unavailable && !noAccessMention && notificationHref(item) !== '';
  const target = rowTarget(item);
  const badge = isLegacySubmissionPing(item)
    ? NOTIFICATION_EVENT_BADGES.sample_photos_uploaded
    : NOTIFICATION_EVENT_BADGES[item.event_type];
  const pipeline = cgPipelineLabel(item);

  return (
    <div className="inbox-detail">
      <div className="inbox-detail-head">
        <Avatar name={actorLabel(item)} imageUrl={item.actor_avatar_url} size="sm" />
        <div className="inbox-detail-who">
          <p className="inbox-detail-actor">{actorLabel(item)}</p>
          <p className="inbox-detail-time">{itemTime(item.created_at)}</p>
        </div>
        <Badge tone={badge.tone} micro>
          {badge.label}
        </Badge>
        {/* The same pipeline badge as the row, so opening one does not lose
            information the list was already showing. */}
        {pipeline === '' ? null : (
          <Badge tone={pipeline === 'In-Stock' ? 'brand' : 'neutral'} micro>
            {pipeline}
          </Badge>
        )}
      </div>

      <p className="inbox-detail-sentence">{itemSentence(item)}</p>

      {target === null ? null : (
        <p className="inbox-detail-target">
          {linkable && target.href !== '' ? (
            <Link className="inbox-detail-link" to={target.href}>
              {target.text}
            </Link>
          ) : (
            target.text
          )}
        </p>
      )}

      {unavailable ? (
        <p className="inbox-detail-note">
          This is no longer available to you. The notification stays as a record of what happened.
        </p>
      ) : noAccessMention ? (
        // Plain and obvious, in Kyu's words, so it does not read as broken:
        // they were mentioned, they know where, and they know why they cannot
        // open it. Asking somebody to add them is the next step, so it says so.
        <p className="inbox-detail-note">
          {actorLabel(item)} mentioned you in {mentionProjectName(item) ?? 'a project'} — you’re not
          on this project, so you cannot open it. Ask {actorLabel(item)} to add you if you need to
          see it.
        </p>
      ) : item.project_name === null ? null : (
        <p className="inbox-detail-note">In {item.project_name}</p>
      )}

      <div className="inbox-detail-actions">
        {linkable && (
          <Button size="sm" onClick={() => onOpen(item)}>
            Open
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={() => onDismiss(item)} disabled={pending}>
          Dismiss
        </Button>
      </div>
    </div>
  );
}

export function InboxPage() {
  /**
   * CG Orders and Messages, each carved out of the general stream.
   *
   * A tab rather than a sixth chip: the chips are a TYPE axis and this is a
   * SOURCE axis, and a chip would have left "All" still meaning "including
   * CG" — so the general stream would never have got any quieter, which is
   * the whole reason for the change. Messages (npc r3, Kyu 28 Sept) is the
   * same shape for the same reason: chats must not bury the rest.
   */
  const [tab, setTab] = useState<InboxTabKey>('general');
  const [filter, setFilter] = useState<NotificationFilterKey>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState('');

  const inbox = useInbox();
  const setRead = useSetNotificationRead();
  const navigate = useNavigate();
  const markAll = useMarkAllNotificationsRead();
  const clearRead = useClearReadNotifications();
  const dismiss = useDeleteNotification();

  const items = inbox.data ?? [];
  const inTab = filterInboxByTab(items, tab);
  const tabFilters = filtersForTab(tab);
  // A filter the current tab does not offer would otherwise stick after
  // switching tabs and silently narrow the list to nothing.
  const activeFilter = tabFilters.some((entry) => entry.key === filter) ? filter : 'all';
  const visible = filterInboxItems(inTab, activeFilter);
  // ONE number, across every tab. Kyu: the sidebar should still behave like
  // one Inbox destination. Two counts would imply two places to go, and
  // whichever you were not looking at would be the one that mattered.
  const unread = unreadCount(items);
  const anyRead = items.some((item) => item.is_read);
  const selected = visible.find((item) => item.id === selectedId) ?? null;

  function report(cause: unknown) {
    setError(cause instanceof Error ? cause.message : 'That could not be saved. Try again.');
  }

  function toggleRead(item: InboxItem) {
    setError('');
    setRead.mutate({ notificationId: item.id, isRead: !item.is_read }, { onError: report });
  }

  function dismissItem(item: InboxItem) {
    setError('');
    if (selectedId === item.id) {
      setSelectedId(null);
    }
    dismiss.mutate(item.id, { onError: report });
  }

  /** Selecting reads it: opening a notification is what "seen" means here. */
  function selectItem(item: InboxItem) {
    setSelectedId(item.id);
    if (!item.is_read) {
      setRead.mutate({ notificationId: item.id, isRead: true });
    }
  }

  function openItem(item: InboxItem) {
    if (notificationHref(item) === '') {
      return;
    }
    if (!item.is_read) {
      setRead.mutate({ notificationId: item.id, isRead: true });
    }
    const href = notificationHref(item);
    // Empty means the row points at nothing readable — a project that was
    // deleted, or an event shape this build does not route. Navigating to ''
    // would reload the current page and look like a dead click.
    if (href === '') return;
    void navigate(href);
  }

  const pending =
    setRead.isPending || dismiss.isPending || markAll.isPending || clearRead.isPending;

  function list() {
    if (inbox.isPending) {
      return <Skeleton lines={3} label="Loading inbox" />;
    }
    if (inbox.isError) {
      return (
        <EmptyState
          title="Inbox could not be loaded"
          description="Something went wrong. Try again in a moment."
        />
      );
    }
    if (items.length === 0) {
      return (
        <EmptyState
          title="You're all caught up"
          description="Assignments, mentions and due-date reminders arrive here."
        />
      );
    }
    if (visible.length === 0) {
      return <EmptyState title="Nothing matches this filter" />;
    }
    return (
      <ul className="inbox-list">
        {visible.map((item) => (
          <InboxRow
            key={item.id}
            item={item}
            selected={item.id === selectedId}
            onToggleRead={toggleRead}
            onDismiss={dismissItem}
            onSelect={selectItem}
            pending={pending}
          />
        ))}
      </ul>
    );
  }

  /** The reading pane earns its space only when there is a list to read. */
  const showDetail = !inbox.isPending && !inbox.isError && visible.length > 0;

  return (
    <>
      <PageHeader title="Inbox" topbar />
      <Panel
        title="Notifications"
        actions={
          <div className="inbox-header-actions">
            {unread === 0 ? null : <Badge tone="brand">{unread} unread</Badge>}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setError('');
                markAll.mutate(undefined, { onError: report });
              }}
              disabled={pending || unread === 0}
            >
              Mark all read
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setError('');
                clearRead.mutate(undefined, { onError: report });
              }}
              disabled={pending || !anyRead}
            >
              Clear read
            </Button>
          </div>
        }
      >
        {error === '' ? null : <InlineError>{error}</InlineError>}

        <nav className="tracker-tab-nav" aria-label="Inbox sections">
          {INBOX_TABS.map((option) => {
            const count = unreadCount(filterInboxByTab(items, option.key));
            return (
              <button
                key={option.key}
                type="button"
                className={`tracker-tab-button${
                  tab === option.key ? ' tracker-tab-button--active' : ''
                }`}
                onClick={() => {
                  setTab(option.key);
                  // Nothing is selected on arrival anywhere else in this page
                  // either — auto-selecting would mark a row read on a click
                  // that was only meant to change tabs.
                  setSelectedId(null);
                }}
              >
                {option.label}
                {/* The shared tab count, not a new one. .tracker-tab-badge is
                    already this exact object — same tokens, same values — and
                    the radius guard caught the duplicate before it shipped. */}
                {count === 0 ? null : <span className="tracker-tab-badge">{count}</span>}
              </button>
            );
          })}
        </nav>

        <div className="inbox-filters" role="group" aria-label="Filter notifications">
          {tabFilters.map((option) => (
            <button
              key={option.key}
              type="button"
              className={
                activeFilter === option.key ? 'inbox-filter inbox-filter--active' : 'inbox-filter'
              }
              aria-pressed={activeFilter === option.key}
              onClick={() => setFilter(option.key)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className={`inbox-split${showDetail ? '' : ' inbox-split--single'}`}>
          <div className="inbox-split-list">{list()}</div>
          {showDetail && (
            <div className="inbox-split-detail">
              <InboxDetail
                item={selected}
                onOpen={openItem}
                onDismiss={dismissItem}
                pending={pending}
              />
            </div>
          )}
        </div>
      </Panel>
    </>
  );
}
